import React, { useState } from "react";
import { CircleDot, ChevronDown, ArrowUpRight } from "lucide-react";
import { PROTON_DRIVE_AFFILIATE_URL } from "../lib/proton";

// Members see this on a session or Fireside card when the keeper turned on "may be recorded".
export const RecordingNotice = ({ testid }) => (
  <p className="mt-2 font-body text-[11px] text-zinc-400 flex items-start gap-1.5" data-testid={testid}>
    <CircleDot size={12} className="text-red-400 shrink-0 mt-0.5" />
    <span>This gathering may be recorded. By joining, you agree to be captured for the replay.</span>
  </p>
);

export const RecordToggle = ({ on, setOn, testid }) => (
  <label className="flex items-start gap-2 font-body text-xs text-zinc-300 cursor-pointer">
    <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid={testid} className="accent-[#14A89A] mt-0.5" />
    <span><CircleDot size={12} className="inline text-red-400 -mt-0.5 mr-1" />This gathering may be recorded <span className="text-zinc-500">(members see a short consent notice before they join)</span></span>
  </label>
);

export const RECORD_STEPS = [
  ["Record in Proton Meet", "Recording needs Meet Professional, an add-on for nearly all Proton plans, included in Workspace and Visionary. The free plan gives one hour calls with up to 50 people, but no record button. The recording saves to your own device, so no one sits between you and the capture."],
  ["Save it to Proton Drive", "Upload the file to Proton Drive and create an encrypted share link. You stay the keeper of your community's faces and voices."],
  ["Post the recap here", "After the session ends, tap Post recap on its card. Paste the Drive link, or upload the video straight into your Hearth, encrypted."],
];

const Steps = () => (
  <ol className="space-y-2.5">
    {RECORD_STEPS.map(([t, b], i) => (
      <li key={t} className="flex items-start gap-2.5">
        <span className="shrink-0 w-5 h-5 rounded-full border border-[#14A89A]/50 text-[#14A89A] font-body text-[11px] flex items-center justify-center">{i + 1}</span>
        <div><div className="font-body text-xs text-white">{t}</div><div className="font-body text-[11px] text-zinc-400 leading-relaxed mt-0.5">{b}</div></div>
      </li>
    ))}
  </ol>
);

const Consent = () => (
  <p className="font-body text-[11px] text-zinc-400 leading-relaxed">
    <span className="text-white">Consent first.</span> Turn on "This gathering may be recorded" when you schedule it, and say so out loud when you start. A clear notice turns a privacy gray zone into a trust signal.
  </p>
);

const ProtonLink = ({ testid }) => (
  <a href={PROTON_DRIVE_AFFILIATE_URL} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-1 font-body text-[11px] text-[#14A89A] hover:text-[#2BC4B5]">Get Proton Drive <ArrowUpRight size={12} /></a>
);

// Collapsible tip inside the Post recap form.
export const RecordingTip = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-[#121212]" data-testid="recap-recording-tip">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="recap-recording-tip-toggle"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 font-body text-xs text-zinc-300 hover:text-white">
        <span className="flex items-center gap-1.5"><CircleDot size={12} className="text-red-400" /> How to record and share your session</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-3"><Steps /><Consent /><ProtonLink testid="recap-proton-link" /></div>}
    </div>
  );
};

// Full section on the Encrypted Meet Up page.
export const RecordingSection = () => (
  <div className="border border-white/10 bg-[#121212] mb-10" data-testid="meetup-recording">
    <div className="px-5 py-3 font-body text-[10px] tracking-[0.25em] uppercase text-zinc-500 border-b border-white/5">Recording your gatherings</div>
    <div className="px-5 py-4 space-y-4">
      <p className="font-body text-xs text-zinc-400 leading-relaxed">Proton Meet records to your own device instead of a company cloud. There's no third party between you and the capture, and you choose how to share it.</p>
      <Steps />
      <Consent />
      <ProtonLink testid="meetup-recording-proton-link" />
    </div>
  </div>
);
